import React, { useState, useEffect, ChangeEvent } from 'react';
import { useSetBorrowerAddressMutation, useGetLoanQuery } from 'generated/graphql';
import { getLoanApplicationParams, validateZipCode } from 'utils/utils';
import { useNavigation } from 'hooks/index';
import { addressLegacySchema } from 'validation-schemas/balcao';
import AddressLayout from './address-layout';
import { optionType, UseGetCitiesAndStatesOptions } from 'hooks/getCitiesAndStatesOptions';
import { endpoint } from 'env';
import axios from 'axios';

export interface ViaCEPResponse {
    logradouro: string;
    bairro: string;
    uf: string;
    localidade: string;
}

const AddressConfiguration: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [addressInput, setAddressInput] = useState<ViaCEPResponse>();
    const [zipCode, setZipCode] = useState<string>();
    const [selectedUf, setSelectedUf] = useState<optionType>();
    const [selectedCity, setSelectedCity] = useState<optionType>();
    const [isValidFields, setIsValidFields] = useState(false);
    const [stateAndCity, setStateAndCity] = useState([]);
    const [isError,setIsError] = useState(false);
    const [formData, setFormData] = useState({
        street:'',
        number:'',
        complement:'',
        neighbourhood:'',
    })

    const [setBorrowerAddress] = useSetBorrowerAddressMutation();

    const { nextScreen, goToPreviousRoute, goToError } = useNavigation();

    const loanApplicationId = getLoanApplicationParams();

    const { data: { loanApplication } } = useGetLoanQuery({
        variables: { id: loanApplicationId },
        fetchPolicy:"cache-first"
    });

    const {
        statesLoading,
        statesOptions,
        citiesLoading,
        citiesOptions,
        stateSelected,
        citySelected
    } = UseGetCitiesAndStatesOptions(addressInput && addressInput, selectedUf?.value);

    useEffect(() => {

        if (loanApplication.borrower.address) {

            const {
                zipCode,
                street,
                number,
                complement,
                neighbourhood,
                city
            } = loanApplication.borrower.address;

            setZipCode(zipCode);
            setFormData({
                street,
                number,
                complement,
                neighbourhood
            })
            setStateAndCity([
                { value: city.state.id, label: city.state.name },
                { value: city.id, label: city.name }
            ])

        }
    }, [loanApplication])

    useEffect(() => {
        if (stateSelected && citySelected) {
            setLoading(false);
            setSelectedUf(stateSelected);
            setSelectedCity(citySelected);
            setFormData({
                street: addressInput.logradouro,
                neighbourhood: addressInput.bairro,
                number: "",
                complement: ""
            })
        }
    }, [addressInput, stateSelected, citySelected]);

    useEffect(() => {
        const validate = addressLegacySchema.isValidSync({
            zipCode,
            street: formData.street,
            number: formData.number,
            neighbourhood:formData.neighbourhood,
            city: citySelected ? citySelected?.value : stateAndCity[1]?.value,
            state: stateSelected ? stateSelected?.value : stateAndCity[0]?.value
        })

        setIsValidFields(validate);
    }, [
        zipCode,
        formData,
        citySelected,
        stateSelected,
        stateAndCity
    ])

    useEffect(() => {
        if (citiesOptions && statesOptions) {
            setSelectedUf(stateSelected ? stateSelected : stateAndCity[0]);
            setSelectedCity(citySelected ? citySelected : stateAndCity[1]);
        }
    }, [citiesOptions, statesOptions, citySelected, stateSelected])

    useEffect(()=> {
        if(isError) {
            setFormData({
                street: "",
                number: "",
                complement: "",
                neighbourhood: ""
            })
            setSelectedCity(null);
            setStateAndCity(null)
        }
    },[isError])

    const handleBlur = (async (e: ChangeEvent<HTMLInputElement>) => {
        try {
            const { value } = e.currentTarget
            const isValidCEP = validateZipCode(value);

            if (isValidCEP) {
                const { data } = await axios.get(`${endpoint.VIACEP_API}${zipCode}/json`);

                if (addressInput?.logradouro === data.logradouro) {
                    setLoading(false);
                } else if (!data.erro) {
                    setAddressInput(data);
                    setLoading(true);
                } else {
                    setLoading(false);
                    setIsError(true);
                }
            }
        } catch (error) {
        setLoading(false);
    }
});

const handleInputChange = (e:ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]:value });
}

const handleSubmit = async () => {
    try {
        if (isValidFields) {
            setBorrowerAddress({
                variables: {
                    loanApplicationId: loanApplication.id,
                    address: {
                        cityId: selectedCity.value,
                        street: formData.street,
                        number: formData.number,
                        complement: formData.complement,
                        neighbourhood: formData.neighbourhood,
                        zipCode: zipCode.replace(new RegExp('[-]', 'g'), ''),
                    }
                }
            });

            nextScreen();
        }
    } catch (error) {
        goToError();
    }
};

return (
    <AddressLayout
        {...{
            zipCode,
            setZipCode,
            selectedUf,
            setSelectedUf,
            selectedCity,
            setSelectedCity,
            goToPreviousRoute,
            handleSubmit,
            statesLoading,
            statesOptions,
            citiesLoading,
            citiesOptions,
            loading,
            isValidFields,
            handleBlur,
            handleInputChange,
            formData
        }}
    />
)
};

export default AddressConfiguration;
